/**
 * Shadow maps — the original's pass 0 (docs/original-engine/renderer.md §2.3).
 *
 * What the specification pins down, and is reproduced here exactly:
 *
 *   - one depth-only surface per light, `DEPTH_COMPONENT16` at 2048x2048
 *     (graphics_light.cpp:167-176);
 *   - created with filtering on and depth comparison on, i.e.
 *     `TEXTURE_COMPARE_MODE = COMPARE_REF_TO_TEXTURE` with `LEQUAL`
 *     (opengl_renderer3d.cpp:1530-1533), which is what makes `textureProj` in
 *     lighting.frag a hardware PCF tap rather than a plain depth read;
 *   - drawn with the **zphase** program, the same one the (hard-disabled) pre-Z pass
 *     used, so nothing new is compiled;
 *   - colour writes off, depth test `LESS`, depth write on, depth cleared and colour
 *     not (r3d_messages.cpp:292-350);
 *   - **front**-face culling, not back — the original's way of pushing shadow acne
 *     onto surfaces that face away from the camera (r3d_messages.cpp:326);
 *   - updated on alternate frames only (graphics_task.cpp:145, 193-194). The caller
 *     owns that counter; see DeferredPipeline.render.
 *   - the matrix handed to lighting.frag is `bias * lightProjection * lightView`,
 *     where `bias` maps NDC to [0,1] (opengl_renderer3d.cpp:200-203).
 *
 * What the specification does NOT pin down, so it is chosen here and flagged:
 *
 *   - the light camera's frustum. renderer.md documents the shadow FBO and the pass
 *     state but not `graphics_light.cpp`'s projection setup, so the volume below is
 *     ours: an orthographic box of half-width `radius` centred on `centre`, with the
 *     camera parked two radii back along the light direction and near/far at one and
 *     three radii. A directional light has no position that matters, only a
 *     direction, so the light's own distance is deliberately ignored — using it would
 *     make the depth range depend on how far away the sun happens to be placed.
 *   - the default volume covers the whole pitch, which keeps the matrix constant and
 *     therefore keeps shadow edges from crawling as the camera pans. It is settable
 *     so a caller can trade coverage for sharpness.
 *
 * One unavoidable divergence: the original sets draw buffers to `{NONE}` and has no
 * colour attachment at all. Three.js has no depth-only render target — every
 * WebGLRenderTarget allocates a colour texture — so the target carries a single-channel
 * 8-bit texture (4 MB at 2048², against 8 MB for the depth) and the caster material
 * turns colour writes off. Nothing is ever written to it or read from it.
 */

import {
  BackSide,
  DepthTexture,
  LessDepth,
  LessEqualCompare,
  LinearFilter,
  Matrix4,
  NearestFilter,
  NoBlending,
  OrthographicCamera,
  RawShaderMaterial,
  RedFormat,
  UnsignedByteType,
  UnsignedShortType,
  Vector2,
  Vector3,
  WebGLRenderTarget,
} from 'three';
import { programMaterial } from './program';

/** graphics_light.cpp:167 — the original's shadow map is 2048x2048. */
export const SHADOW_MAP_SIZE = 2048;

/**
 * Ceiling on how many lights get their own map. The original allocates one per
 * (light x camera) with no limit, but it only ever has one directional sun; the cap is
 * here so that a caller adding fill lights cannot quietly cost 12 MB apiece.
 */
export const MAX_SHADOW_MAPS = 4;

/**
 * The alternate-frame skip: the original redraws its shadow maps on every other frame
 * (`shadowSkipFrameCounter`, graphics_task.cpp:145, 193-194). Halves the cost of the pass,
 * and is invisible because neither the sun nor the shadow volume moves.
 *
 * The frames in between still *sample* the map — they just sample the one drawn last frame,
 * with the matrix it was drawn with.
 */
export function shouldRedrawShadows(frame: number): boolean {
  return frame % 2 === 0;
}

/**
 * NDC [-1,1] -> texture [0,1], the `bias` half of `lightViewProjectionMatrix`
 * (opengl_renderer3d.cpp:200-203). Matrix4.set takes its arguments in row order.
 */
const NDC_TO_TEXTURE = /* @__PURE__ */ new Matrix4().set(
  0.5, 0.0, 0.0, 0.5,
  0.0, 0.5, 0.0, 0.5,
  0.0, 0.0, 0.5, 0.5,
  0.0, 0.0, 0.0, 1.0,
);

/** The region of the world the shadow map covers, in RENDER space (Y up). */
export interface ShadowVolume {
  /** Centre of the shadowed region. */
  readonly centre: Vector3;
  /** Half-width of the orthographic box around it, in metres. */
  readonly radius: number;
}

/**
 * Covers the whole playing surface and a margin of stadium floor. The pitch is 110 x 72
 * centred on the origin, so its corners sit 66 m out; 70 gives room for the light to be
 * off-axis. At 2048 texels that is 6.8 cm per texel.
 */
export const DEFAULT_SHADOW_VOLUME: ShadowVolume = {
  centre: new Vector3(0, 0, 0),
  radius: 70,
};

/**
 * How wide the soft part of a shadow's edge is, in metres on the ground, for a caster right
 * up against what it falls on.
 *
 * This is the boot-on-the-grass case, and it wants to stay tight: a player's own shadow is
 * the thing that tells you his feet are on the pitch rather than hovering above it. It is
 * still four times what the original used, which sampled a tenth of a metre across and drew
 * every outline in the ground with a razor.
 */
export const SHADOW_SHARP_RADIUS = 0.12;

/**
 * And the same, for a caster far enough away that the sun stops being a point.
 *
 * A stand roof twenty metres up throws its shadow some twenty-six metres down a low
 * afternoon sun's ray, and the sun is half a degree wide, so the geometry alone spreads the
 * edge over about a quarter of a metre. What is here is wider than that on purpose. A roof
 * edge is not a razor -- it is a fascia, a gutter, a lighting rig and a row of stanchions,
 * each throwing its own slightly different line -- and the light that fills the shade is the
 * sky, which the roof takes away gradually as you walk under it rather than all at once. On
 * a pitch seen from a television camera this is about forty pixels of ramp, which is the
 * difference between a shadow and a line ruled across the grass.
 */
export const SHADOW_SOFT_RADIUS = 0.9;

/**
 * How far up the light's ray a caster has to be before the wide radius is used, in metres.
 *
 * Everything that happens on the pitch itself -- a player, the ball, the goal frame -- is
 * within a couple of metres of the grass. The stand roof is twenty-six. Eight metres is the
 * gap between those two populations, and nothing in a stadium sits in it.
 */
export const SHADOW_BLOCKER_DISTANCE = 8;

/** Up axis for the light camera. Swapped when the light is close to straight overhead. */
const UP_Y = /* @__PURE__ */ new Vector3(0, 1, 0);
const UP_Z = /* @__PURE__ */ new Vector3(0, 0, 1);

/**
 * The caster material: the zphase program with the shadow pass's fixed state on it.
 * Applied through `Scene.overrideMaterial`, so it replaces whatever each mesh normally
 * draws with.
 *
 * `side = BackSide` is three.js's spelling of front-face culling. `colorWrite = false`
 * stands in for the original's `{NONE}` draw buffers.
 */
export function createShadowCasterMaterial(): RawShaderMaterial {
  const material = programMaterial('zphase', {});
  material.side = BackSide;
  material.depthTest = true;
  material.depthWrite = true;
  material.depthFunc = LessDepth; // the original uses LESS, three defaults to LEQUAL
  material.colorWrite = false;
  material.blending = NoBlending;
  return material;
}

export class ShadowMap {
  readonly target: WebGLRenderTarget;
  readonly depth: DepthTexture;
  readonly camera = new OrthographicCamera(-1, 1, 1, -1, 1, 3);

  /**
   * `NDC_TO_TEXTURE * projection * view` for the pose the map was last drawn from.
   *
   * Held separately from the camera because the map is only redrawn on alternate
   * frames: on a skipped frame the lighting pass must keep projecting against the pose
   * the depth values actually came from, or the shadows slide off their casters.
   */
  readonly viewProjection = new Matrix4();

  /**
   * What one metre of the world is worth in this map's own units: `x` across it, in texture
   * coordinates, `y` into it, in the depth the comparison sampler stores.
   *
   * `lighting.frag` needs both — it measures its sampling radius and its blocker probe in
   * metres, because that is the only way to say "a boot's shadow is sharp and a roof's is
   * not" and have it stay true when the volume is resized. Both fall out of the box `aim`
   * builds: it is `2 * radius` metres wide and, since near and far sit at one and three
   * radii, `2 * radius` metres deep as well.
   */
  readonly unitsPerMetre = new Vector2();

  /**
   * False until the map has actually been drawn into once. A light that appears on a frame
   * the alternate-frame skip is skipping gets its map allocated but not filled, and sampling
   * it in that state would shadow the scene with whatever the depth buffer happened to hold.
   */
  rendered = false;

  private readonly direction = new Vector3();

  constructor() {
    this.depth = new DepthTexture(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE, UnsignedShortType);
    // Linear filtering plus comparison mode is what turns each textureProj into a
    // hardware PCF tap. The original passes filter = true here (graphics_light.cpp:167).
    this.depth.minFilter = LinearFilter;
    this.depth.magFilter = LinearFilter;
    this.depth.compareFunction = LessEqualCompare;
    // The original also applies max anisotropy to this texture, which renderer.md
    // §1.3(j) calls out as pointless on a shadow map. Not reproduced.

    this.target = new WebGLRenderTarget(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE, {
      // Placeholder colour attachment only; see the note at the top of this file.
      format: RedFormat,
      type: UnsignedByteType,
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      depthBuffer: true,
      depthTexture: this.depth,
      generateMipmaps: false,
    });
  }

  /**
   * Point the light camera at the shadow volume and rebuild `viewProjection`.
   *
   * `lightPosition` is read for its direction from the volume centre only.
   */
  aim(lightPosition: Vector3, volume: ShadowVolume): void {
    const radius = Math.max(1e-3, volume.radius);

    this.direction.subVectors(lightPosition, volume.centre);
    if (this.direction.lengthSq() < 1e-12) this.direction.copy(UP_Y);
    this.direction.normalize();

    const camera = this.camera;
    camera.position.copy(volume.centre).addScaledVector(this.direction, radius * 2);
    // lookAt cannot build a basis when the view direction is parallel to up.
    camera.up.copy(Math.abs(this.direction.y) > 0.99 ? UP_Z : UP_Y);
    camera.lookAt(volume.centre);

    camera.left = -radius;
    camera.right = radius;
    camera.top = radius;
    camera.bottom = -radius;
    // A slab one radius either side of the centre. Keeping the range this tight is what
    // makes 16-bit depth enough, and it fixes the physical size of lighting.frag's
    // hard-coded -0.0002 depth offset at roughly 2.8 cm for the default volume.
    camera.near = radius;
    camera.far = radius * 3;
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld(true);

    this.viewProjection
      .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
      .premultiply(NDC_TO_TEXTURE);

    // Both axes span 2 * radius metres over the one unit the bias matrix maps them onto.
    this.unitsPerMetre.set(0.5 / radius, 0.5 / radius);
  }

  dispose(): void {
    this.target.dispose();
    this.depth.dispose();
  }
}
